import { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import FuseLayout from '@fuse/core/FuseLayout';
import themeLayouts from '@/components/theme-layouts/themeLayouts';
import ProtectedRoute from '@/components/common/ProtectedRoute';

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));

const AssetsRegisterPage = lazy(() => import('@/pages/assets/assets-register/AssetsRegisterPage'));
const AddAssetPage = lazy(() => import('@/pages/assets/assets-register/asset/add/AddAssetPage'));
const EditAssetPage = lazy(() => import('@/pages/assets/assets-register/asset/edit/EditAssetPage'));
const OverviewAssetPage = lazy(() => import('@/pages/assets/assets-register/asset/overview/OverviewAssetPage'));
const AddCategoryPage = lazy(() => import('@/pages/assets/assets-register/category/add/AddCategoryPage'));
const EditCategoryPage = lazy(() => import('@/pages/assets/assets-register/category/edit/EditCategoryPage'));
const CategoryOverviewPage = lazy(
	() => import('@/pages/assets/assets-register/category/overview/CategoryOverviewPage'),
);
const AddTypePage = lazy(() => import('@/pages/assets/assets-register/type/add/AddTypePage'));
const EditTypePage = lazy(() => import('@/pages/assets/assets-register/type/edit/EditTypePage'));
const TypeOverviewPage = lazy(() => import('@/pages/assets/assets-register/type/overview/TypeOverviewPage'));
const AddGroupPage = lazy(() => import('@/pages/assets/assets-register/group/add/AddGroupPage'));
const EditGroupPage = lazy(() => import('@/pages/assets/assets-register/group/edit/EditGroupPage'));
const GroupOverviewPage = lazy(() => import('@/pages/assets/assets-register/group/overview/GroupOverviewPage'));

const DocumentsPage = lazy(() => import('@/pages/assets/documents/DocumentsPage'));
const AddDocumentPage = lazy(() => import('@/pages/assets/documents/add/AddDocumentPage'));
const EditDocumentPage = lazy(() => import('@/pages/assets/documents/edit/EditDocumentPage'));
const DocumentOverviewPage = lazy(() => import('@/pages/assets/documents/overview/DocumentOverviewPage'));
const ServiceContractsPage = lazy(() => import('@/pages/assets/service-contracts/ServiceContractsPage'));
const AddServiceContractPage = lazy(() => import('@/pages/assets/service-contracts/add/AddServiceContractPage'));
const ServiceContractOverviewPage = lazy(
	() => import('@/pages/assets/service-contracts/overview/ServiceContractOverviewPage'),
);
const EditServiceContractPage = lazy(() => import('@/pages/assets/service-contracts/edit/EditServiceContractPage'));
const AddServicePartnerPage = lazy(() => import('@/pages/assets/service-contracts/partners/add/AddServicePartnerPage'));
const EditServicePartnerPage = lazy(
	() => import('@/pages/assets/service-contracts/partners/edit/EditServicePartnerPage'),
);
const ServicePartnerOverviewPage = lazy(
	() => import('@/pages/assets/service-contracts/partners/overview/ServicePartnerOverviewPage'),
);

// Procedures
const ProceduresPage = lazy(() => import('@/pages/procedures/ProceduresPage'));
const AddProcedurePage = lazy(() => import('@/pages/procedures/add/AddProcedurePage'));
const EditProcedurePage = lazy(() => import('@/pages/procedures/edit/EditProcedurePage'));
const ProcedureOverviewPage = lazy(() => import('@/pages/procedures/overview/ProcedureOverviewPage'));

const RulesPage = lazy(() => import('@/pages/rules/RulesPage'));
const AddTimeBasedRulePage = lazy(() => import('@/pages/rules/add/time-based/AddTimeBasedRulePage'));
const AddUsageBasedRulePage = lazy(() => import('@/pages/rules/add/usage-based/AddUsageBasedRulePage'));
const RuleOverviewPage = lazy(() => import('@/pages/rules/overview/RuleOverviewPage'));
const EditTimeBasedRulePage = lazy(() => import('@/pages/rules/edit/time-based/EditTimeBasedRulePage'));
const EditUsageBasedRulePage = lazy(() => import('@/pages/rules/edit/usage-based/EditUsageBasedRulePage'));

const WorkOrdersPage = lazy(() => import('@/pages/work-orders/WorkOrdersPage'));
const AddWorkOrderPage = lazy(() => import('@/pages/work-orders/add/AddWorkOrderPage'));
const WorkOrderOverviewPage = lazy(() => import('@/pages/work-orders/overview/WorkOrderOverviewPage'));
const EditManualWorkOrderPage = lazy(() => import('@/pages/work-orders/edit/EditManualWorkOrderPage'));
const ExecuteProcedurePage = lazy(() => import('@/pages/work-orders/procedure/ExecuteProcedurePage'));
const EditRuleBasedWorkOrderPage = lazy(() => import('@/pages/work-orders/edit/EditRuleBasedWorkOrderPage'));

const SitesPage = lazy(() => import('@/pages/administration/sites/SitesPage'));
const AddSitePage = lazy(() => import('@/pages/administration/sites/add/AddSitePage'));
const SiteOverviewPage = lazy(() => import('@/pages/administration/sites/overview/SiteOverviewPage'));
const EditSitePage = lazy(() => import('@/pages/administration/sites/edit/EditSitePage'));
const AreasPage = lazy(() => import('@/pages/administration/areas/AreasPage'));
const AddAreaPage = lazy(() => import('@/pages/administration/areas/add/AddAreaPage'));
const AreaOverviewPage = lazy(() => import('@/pages/administration/areas/overview/AreaOverviewPage'));
const EditAreaPage = lazy(() => import('@/pages/administration/areas/edit/EditAreaPage'));
const DepartmentsPage = lazy(() => import('@/pages/administration/departments/DepartmentsPage'));
const AddDepartmentPage = lazy(() => import('@/pages/administration/departments/add/AddDepartmentPage'));
const DepartmentOverviewPage = lazy(() => import('@/pages/administration/departments/overview/DepartmentOverviewPage'));
const EditDepartmentPage = lazy(() => import('@/pages/administration/departments/edit/EditDepartmentPage'));
const UsersPage = lazy(() => import('@/pages/administration/users/UsersPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const PrivacyPolicyPage = lazy(() => import('@/pages/public/privacy-policy/PrivacyPolicyPage'));
const QrAssetRedirectPage = lazy(() => import('@/pages/public/qr-asset-redirect/QrAssetRedirectPage'));

// Platform (cross-organisation, GlobalAdmin-only)
const OrganisationsPage = lazy(() => import('@/pages/platform/organisations/OrganisationsPage'));
const OrganisationOverviewPage = lazy(() => import('@/pages/platform/organisations/OrganisationOverviewPage'));
const EditOrganisationPage = lazy(() => import('@/pages/platform/organisations/EditOrganisationPage'));
const CreateOrganisationPage = lazy(() => import('@/pages/platform/organisations/CreateOrganisationPage'));

const ResourcesPage = lazy(() => import('@/pages/resourcing/resources/ResourcesPage'));
const AddResourcePage = lazy(() => import('@/pages/resourcing/resources/add/AddResourcePage'));
const EditResourcePage = lazy(() => import('@/pages/resourcing/resources/edit/EditResourcePage'));
const ResourceOverviewPage = lazy(() => import('@/pages/resourcing/resources/overview/ResourceOverviewPage'));
const MaintenanceTeamsPage = lazy(() => import('@/pages/resourcing/maintenance-teams/MaintenanceTeamsPage'));
const AddMaintenanceTeamPage = lazy(() => import('@/pages/resourcing/maintenance-teams/add/AddMaintenanceTeamPage'));
const EditMaintenanceTeamPage = lazy(() => import('@/pages/resourcing/maintenance-teams/edit/EditMaintenanceTeamPage'));
const MaintenanceTeamOverviewPage = lazy(
	() => import('@/pages/resourcing/maintenance-teams/overview/MaintenanceTeamOverviewPage'),
);
const ResourceInsightsPage = lazy(() => import('@/pages/resourcing/resource-insights/ResourceInsightsPage'));
const DisciplinesPage = lazy(() => import('@/pages/resourcing/disciplines/DisciplinesPage'));
const AddDisciplinePage = lazy(() => import('@/pages/resourcing/disciplines/add/AddDisciplinePage'));
const EditDisciplinePage = lazy(() => import('@/pages/resourcing/disciplines/edit/EditDisciplinePage'));
const DisciplineOverviewPage = lazy(() => import('@/pages/resourcing/disciplines/overview/DisciplineOverviewPage'));

const ASSETS_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner', 'FieldTech'];
const ASSETS_WRITE_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin'];
const RULES_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
const PROCEDURES_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
const WORK_ORDER_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner', 'FieldTech'];
// Creating and editing work orders is Planner+, as the API's CanManageWorkOrders policy.
const WORK_ORDER_WRITE_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
const ADMIN_ROLES = ['GlobalAdmin', 'OrganisationAdmin'];
// Planner: read-only; writes gated server-side by CanManageResourcing (Admin+).
const RESOURCING_READ_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
const RESOURCING_WRITE_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin'];
// Platform-only surface (cross-organisation tenant management) — GlobalAdmin only, never OrganisationAdmin.
const PLATFORM_ROLES = ['GlobalAdmin'];

const routesConfig: RouteObject[] = [
	{
		// Public route — deliberately outside ProtectedRoute so it renders without a sign-in.
		path: '/privacy-policy',
		element: (
			<Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
				<PrivacyPolicyPage />
			</Suspense>
		),
	},
	{
		// Public: reached only when the mobile app isn't installed/verified
		path: '/qr/asset/:id',
		element: (
			<Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
				<QrAssetRedirectPage />
			</Suspense>
		),
	},
	{
		path: '/',
		element: (
			<ProtectedRoute>
				<Suspense
					fallback={
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }} />
					}
				>
					<FuseLayout layouts={themeLayouts} />
				</Suspense>
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: (
					<Navigate
						to="dashboard"
						replace
					/>
				),
			},
			{
				path: 'dashboard',
				element: <DashboardPage />,
			},
			{
				path: 'assets-register',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<AssetsRegisterPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/add-asset',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddAssetPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/asset/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<OverviewAssetPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/add-category',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddCategoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/category/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<CategoryOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/add-type',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddTypePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/type/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<TypeOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/add-group',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddGroupPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/groups/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<GroupOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/edit-asset/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditAssetPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/edit-category/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditCategoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/edit-type/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditTypePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets-register/groups/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditGroupPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'documents',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<DocumentsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'documents/add',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddDocumentPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'documents/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<DocumentOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'documents/edit/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditDocumentPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<ServiceContractsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/add',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddServiceContractPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<ServiceContractOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/edit/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditServiceContractPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/partners/add',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddServicePartnerPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/partners/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_ROLES}>
						<ServicePartnerOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'service-contracts/partners/edit/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditServicePartnerPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'procedures',
				element: (
					<ProtectedRoute requiredRoles={PROCEDURES_ROLES}>
						<ProceduresPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'procedures/add',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddProcedurePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'procedures/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={PROCEDURES_ROLES}>
						<ProcedureOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'procedures/edit/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditProcedurePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules',
				element: (
					<ProtectedRoute requiredRoles={RULES_ROLES}>
						<RulesPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules/add/time-based',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddTimeBasedRulePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules/add/usage-based',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<AddUsageBasedRulePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={RULES_ROLES}>
						<RuleOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules/edit/time-based/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditTimeBasedRulePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'rules/edit/usage-based/:id',
				element: (
					<ProtectedRoute requiredRoles={ASSETS_WRITE_ROLES}>
						<EditUsageBasedRulePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_ROLES}>
						<WorkOrdersPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders/add',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_WRITE_ROLES}>
						<AddWorkOrderPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders/overview/:id',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_ROLES}>
						<WorkOrderOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				// Running one attached procedure. WORK_ORDER_ROLES, not the procedure-admin roles:
				// this is the technician doing the job, not somebody editing the procedure library.
				path: 'work-orders/overview/:id/procedures/:procedureId',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_ROLES}>
						<ExecuteProcedurePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders/edit/:id',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_WRITE_ROLES}>
						<EditManualWorkOrderPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders/edit-rule/:id',
				element: (
					<ProtectedRoute requiredRoles={WORK_ORDER_WRITE_ROLES}>
						<EditRuleBasedWorkOrderPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'sites',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<SitesPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'sites/add',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<AddSitePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'sites/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<EditSitePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'sites/:id',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<SiteOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'areas',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<AreasPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'areas/add',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<AddAreaPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'areas/:id',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<AreaOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'areas/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<EditAreaPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'departments',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<DepartmentsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'departments/add',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<AddDepartmentPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'departments/:id',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<DepartmentOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'departments/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<EditDepartmentPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'users',
				element: (
					<ProtectedRoute requiredRoles={ADMIN_ROLES}>
						<UsersPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'resources',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<ResourcesPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'resources/add',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<AddResourcePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'resources/:id',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<ResourceOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'resources/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<EditResourcePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'maintenance-teams',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<MaintenanceTeamsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'maintenance-teams/add',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<AddMaintenanceTeamPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'maintenance-teams/:id',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<MaintenanceTeamOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'maintenance-teams/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<EditMaintenanceTeamPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'resource-insights',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<ResourceInsightsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'disciplines',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<DisciplinesPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'disciplines/add',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<AddDisciplinePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'disciplines/:id',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_READ_ROLES}>
						<DisciplineOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'disciplines/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={RESOURCING_WRITE_ROLES}>
						<EditDisciplinePage />
					</ProtectedRoute>
				),
			},
			{
				path: 'organisations',
				element: (
					<ProtectedRoute requiredRoles={PLATFORM_ROLES}>
						<OrganisationsPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'organisations/new',
				element: (
					<ProtectedRoute requiredRoles={PLATFORM_ROLES}>
						<CreateOrganisationPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'organisations/:id',
				element: (
					<ProtectedRoute requiredRoles={PLATFORM_ROLES}>
						<OrganisationOverviewPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'organisations/:id/edit',
				element: (
					<ProtectedRoute requiredRoles={PLATFORM_ROLES}>
						<EditOrganisationPage />
					</ProtectedRoute>
				),
			},
			{
				path: '*',
				element: <NotFoundPage />,
			},
		],
	},
];

export default routesConfig;
